import React, { useState } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import SentimentVerySatisfiedIcon from "@material-ui/icons/SentimentVerySatisfied";
import SentimentSatisfiedIcon from "@material-ui/icons/SentimentSatisfied";
import SentimentDissatisfiedIcon from "@material-ui/icons/SentimentDissatisfied";
import SentimentVeryDissatisfiedIcon from "@material-ui/icons/SentimentVeryDissatisfied";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import StarIcon from "@material-ui/icons/Star";
import PersonIcon from "@material-ui/icons/Person";
import ChatIcon from "@material-ui/icons/Chat";
import ThumbUpIcon from "@material-ui/icons/ThumbUp";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

const ACCENT = "#4F46E5";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  scoreCard: {
    textAlign: "center",
    position: "relative",
    overflow: "visible",
  },
  mainScoreCard: {
    textAlign: "center",
    borderLeft: `4px solid ${ACCENT}`,
    background: `linear-gradient(135deg, ${ACCENT}08 0%, ${ACCENT}03 100%)`,
  },
  bigScore: {
    fontSize: "3.5rem",
    fontWeight: 800,
    color: ACCENT,
    lineHeight: 1,
  },
  scoreLabel: {
    fontSize: "0.7rem",
    color: "rgba(0,0,0,0.54)",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginTop: 4,
  },
  scoreValue: {
    fontSize: "2rem",
    fontWeight: 700,
  },
  trendUp: { color: "#22c55e", display: "inline-flex", alignItems: "center", fontSize: "0.75rem", fontWeight: 600 },
  trendDown: { color: "#ef4444", display: "inline-flex", alignItems: "center", fontSize: "0.75rem", fontWeight: 600 },
  ratingBar: { marginBottom: theme.spacing(1.5) },
  ratingLabel: { minWidth: 100, display: "flex", alignItems: "center", gap: 4 },
  progressBar: { height: 10, borderRadius: 5, flex: 1, marginLeft: 12, marginRight: 12 },
  paper: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  sectionTitle: { fontWeight: 700, marginBottom: theme.spacing(2), color: "#1e293b" },
  agentAvatar: {
    width: 32,
    height: 32,
    backgroundColor: ACCENT,
    fontSize: "0.8rem",
    fontWeight: 700,
  },
  satisfactionEmoji: {
    fontSize: "2.5rem",
    marginBottom: 8,
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const CSAT_DATA = {
  overall: 4.3,
  previousOverall: 4.1,
  totalResponses: 847,
  responseRate: 62,
  nps: 72,
  previousNps: 68,
  distribution: [
    { stars: 5, label: "Excelente", count: 412, color: "#22c55e" },
    { stars: 4, label: "Bom", count: 198, color: "#84cc16" },
    { stars: 3, label: "Regular", count: 127, color: "#f59e0b" },
    { stars: 2, label: "Ruim", count: 72, color: "#f97316" },
    { stars: 1, label: "Péssimo", count: 38, color: "#ef4444" },
  ],
  byPeriod: [
    { month: "Jan", score: 4.0, responses: 98 },
    { month: "Fev", score: 4.1, responses: 112 },
    { month: "Mar", score: 4.2, responses: 134 },
    { month: "Abr", score: 4.3, responses: 156 },
    { month: "Mai", score: 4.4, responses: 148 },
    { month: "Jun", score: 4.3, responses: 199 },
  ],
  agentRatings: [
    { name: "Maria Silva", avatar: "MS", tickets: 89, avgRating: 4.8, responses: 67, satisfaction: 96 },
    { name: "João Santos", avatar: "JS", tickets: 76, avgRating: 4.6, responses: 58, satisfaction: 92 },
    { name: "Ana Oliveira", avatar: "AO", tickets: 92, avgRating: 4.5, responses: 72, satisfaction: 89 },
    { name: "Carlos Lima", avatar: "CL", tickets: 68, avgRating: 4.3, responses: 45, satisfaction: 85 },
    { name: "Beatriz Costa", avatar: "BC", tickets: 54, avgRating: 4.2, responses: 38, satisfaction: 82 },
    { name: "Pedro Souza", avatar: "PS", tickets: 71, avgRating: 4.0, responses: 52, satisfaction: 78 },
    { name: "Lucas Martins", avatar: "LM", tickets: 63, avgRating: 3.9, responses: 41, satisfaction: 75 },
    { name: "Fernanda Alves", avatar: "FA", tickets: 82, avgRating: 4.7, responses: 65, satisfaction: 94 },
  ],
  recentFeedback: [
    { id: 1, contact: "Ricardo Mendes", rating: 5, comment: "Atendimento excelente! Problema resolvido rapidamente.", agent: "Maria Silva", date: "2026-06-15", ticket: "#1847" },
    { id: 2, contact: "Patrícia Rocha", rating: 4, comment: "Bom atendimento, mas demorou um pouco para responder.", agent: "João Santos", date: "2026-06-15", ticket: "#1845" },
    { id: 3, contact: "Fernando Gomes", rating: 5, comment: "Perfeito! O agente sabia exatamente como resolver.", agent: "Ana Oliveira", date: "2026-06-14", ticket: "#1842" },
    { id: 4, contact: "Camila Dias", rating: 2, comment: "Precisei explicar o problema várias vezes.", agent: "Pedro Souza", date: "2026-06-14", ticket: "#1839" },
    { id: 5, contact: "Roberto Nunes", rating: 5, comment: "Atendimento rápido e eficiente. Parabéns!", agent: "Fernanda Alves", date: "2026-06-13", ticket: "#1836" },
    { id: 6, contact: "Juliana Ferreira", rating: 3, comment: "Resolveram, porém o tempo de espera foi longo.", agent: "Carlos Lima", date: "2026-06-13", ticket: "#1833" },
    { id: 7, contact: "Marcos Vieira", rating: 4, comment: "Muito educado e prestativo.", agent: "Maria Silva", date: "2026-06-12", ticket: "#1830" },
    { id: 8, contact: "Larissa Moreira", rating: 1, comment: "Meu problema não foi resolvido. Precisei ligar novamente.", agent: "Lucas Martins", date: "2026-06-12", ticket: "#1827" },
    { id: 9, contact: "André Barbosa", rating: 5, comment: "Serviço nota 10! Continuem assim.", agent: "Fernanda Alves", date: "2026-06-11", ticket: "#1824" },
    { id: 10, contact: "Thiago Carvalho", rating: 4, comment: "Bom suporte, resolveram minha dúvida.", agent: "João Santos", date: "2026-06-11", ticket: "#1821" },
  ],
};

const CustomerSatisfaction = () => {
  const classes = useStyles();
  const [period, setPeriod] = useState("6m");

  const maxCount = Math.max(...CSAT_DATA.distribution.map((d) => d.count));

  const ratingIcon = (rating) => {
    if (rating >= 4.5) return <SentimentVerySatisfiedIcon style={{ color: "#22c55e" }} />;
    if (rating >= 3.5) return <SentimentSatisfiedIcon style={{ color: "#84cc16" }} />;
    if (rating >= 2.5) return <SentimentDissatisfiedIcon style={{ color: "#f59e0b" }} />;
    return <SentimentVeryDissatisfiedIcon style={{ color: "#ef4444" }} />;
  };

  const starIcons = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon
        key={i}
        style={{
          fontSize: 16,
          color: i < rating ? "#f59e0b" : "#e2e8f0",
        }}
      />
    ));
  };

  return (
    <div className={classes.root}>
      <MainHeader>
        <Title>Satisfação do Cliente (CSAT)</Title>
      </MainHeader>

      <div className={classes.filterRow}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
          <InputLabel>Período</InputLabel>
          <Select value={period} onChange={(e) => setPeriod(e.target.value)} label="Período">
            <MenuItem value="7d">Últimos 7 dias</MenuItem>
            <MenuItem value="30d">Últimos 30 dias</MenuItem>
            <MenuItem value="3m">Últimos 3 meses</MenuItem>
            <MenuItem value="6m">Últimos 6 meses</MenuItem>
            <MenuItem value="1y">Último ano</MenuItem>
          </Select>
        </FormControl>
      </div>

      {/* Main metrics */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={3}>
          <Card className={classes.mainScoreCard}>
            <CardContent>
              <Typography className={classes.bigScore}>{CSAT_DATA.overall}</Typography>
              <Typography className={classes.scoreLabel}>Nota Média CSAT</Typography>
              <Box mt={1}>
                <span className={classes.trendUp}>
                  <TrendingUpIcon style={{ fontSize: 14, marginRight: 2 }} />
                  +{(CSAT_DATA.overall - CSAT_DATA.previousOverall).toFixed(1)} vs período anterior
                </span>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card className={classes.scoreCard} style={{ borderLeft: "4px solid #22c55e" }}>
            <CardContent>
              <Typography className={classes.scoreValue} style={{ color: "#22c55e" }}>
                {CSAT_DATA.nps}
              </Typography>
              <Typography className={classes.scoreLabel}>NPS Score</Typography>
              <Box mt={1}>
                <span className={classes.trendUp}>
                  <TrendingUpIcon style={{ fontSize: 14, marginRight: 2 }} />
                  +{CSAT_DATA.nps - CSAT_DATA.previousNps} pontos
                </span>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card className={classes.scoreCard} style={{ borderLeft: "4px solid #3b82f6" }}>
            <CardContent>
              <Typography className={classes.scoreValue} style={{ color: "#3b82f6" }}>
                {CSAT_DATA.totalResponses}
              </Typography>
              <Typography className={classes.scoreLabel}>Total de Avaliações</Typography>
              <Box mt={1} display="flex" justifyContent="center" alignItems="center">
                <ChatIcon style={{ fontSize: 14, color: "#94a3b8", marginRight: 4 }} />
                <Typography variant="caption" color="textSecondary">
                  Respostas coletadas
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card className={classes.scoreCard} style={{ borderLeft: "4px solid #8b5cf6" }}>
            <CardContent>
              <Typography className={classes.scoreValue} style={{ color: "#8b5cf6" }}>
                {CSAT_DATA.responseRate}%
              </Typography>
              <Typography className={classes.scoreLabel}>Taxa de Resposta</Typography>
              <Box mt={1} display="flex" justifyContent="center" alignItems="center">
                <ThumbUpIcon style={{ fontSize: 14, color: "#94a3b8", marginRight: 4 }} />
                <Typography variant="caption" color="textSecondary">
                  Dos tickets fechados
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Rating distribution */}
        <Grid item xs={5}>
          <Paper className={classes.paper}>
            <Typography className={classes.sectionTitle}>Distribuição de Avaliações</Typography>
            {CSAT_DATA.distribution.map((d) => (
              <Box key={d.stars} className={classes.ratingBar} display="flex" alignItems="center">
                <Box className={classes.ratingLabel}>
                  {Array.from({ length: d.stars }, (_, i) => (
                    <StarIcon key={i} style={{ fontSize: 14, color: "#f59e0b" }} />
                  ))}
                </Box>
                <Box className={classes.progressBar}>
                  <LinearProgress
                    variant="determinate"
                    value={(d.count / maxCount) * 100}
                    style={{ height: 10, borderRadius: 5, backgroundColor: "#f1f5f9" }}
                    classes={{
                      bar: undefined,
                    }}
                  />
                </Box>
                <Typography variant="body2" style={{ minWidth: 65, fontWeight: 600, color: "#475569" }}>
                  {d.count} ({((d.count / CSAT_DATA.totalResponses) * 100).toFixed(0)}%)
                </Typography>
              </Box>
            ))}
            <Box mt={2} pt={2} borderTop="1px solid #e2e8f0">
              <Typography variant="caption" color="textSecondary">
                Média ponderada: <strong style={{ color: ACCENT }}>{CSAT_DATA.overall}</strong> de 5.0
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Trend by month */}
        <Grid item xs={7}>
          <Paper className={classes.paper}>
            <Typography className={classes.sectionTitle}>Evolução Mensal</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 700 }}>Mês</TableCell>
                    <TableCell style={{ fontWeight: 700 }} align="center">Nota Média</TableCell>
                    <TableCell style={{ fontWeight: 700 }} align="center">Respostas</TableCell>
                    <TableCell style={{ fontWeight: 700 }}>Tendência</TableCell>
                    <TableCell style={{ fontWeight: 700 }}>Barra</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {CSAT_DATA.byPeriod.map((m, i) => {
                    const prev = i > 0 ? CSAT_DATA.byPeriod[i - 1].score : m.score;
                    const diff = m.score - prev;
                    return (
                      <TableRow key={m.month} hover>
                        <TableCell style={{ fontWeight: 600 }}>{m.month} 2026</TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            {ratingIcon(m.score)}
                            <Typography variant="body2" style={{ fontWeight: 700, marginLeft: 4 }}>
                              {m.score}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">{m.responses}</TableCell>
                        <TableCell>
                          {diff > 0 ? (
                            <span className={classes.trendUp}>
                              <TrendingUpIcon style={{ fontSize: 14, marginRight: 2 }} />
                              +{diff.toFixed(1)}
                            </span>
                          ) : diff < 0 ? (
                            <span className={classes.trendDown}>
                              <TrendingDownIcon style={{ fontSize: 14, marginRight: 2 }} />
                              {diff.toFixed(1)}
                            </span>
                          ) : (
                            <Typography variant="caption" color="textSecondary">--</Typography>
                          )}
                        </TableCell>
                        <TableCell style={{ width: "30%" }}>
                          <LinearProgress
                            variant="determinate"
                            value={(m.score / 5) * 100}
                            style={{ height: 8, borderRadius: 4, backgroundColor: "#f1f5f9" }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Agent performance */}
      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>Desempenho por Agente</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 700 }}>Agente</TableCell>
                <TableCell style={{ fontWeight: 700 }} align="center">Tickets</TableCell>
                <TableCell style={{ fontWeight: 700 }} align="center">Avaliações</TableCell>
                <TableCell style={{ fontWeight: 700 }} align="center">Nota Média</TableCell>
                <TableCell style={{ fontWeight: 700 }} align="center">Satisfação</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Rating</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {CSAT_DATA.agentRatings
                .sort((a, b) => b.avgRating - a.avgRating)
                .map((agent) => (
                  <TableRow key={agent.name} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar className={classes.agentAvatar}>{agent.avatar}</Avatar>
                        <Typography variant="body2" style={{ fontWeight: 600, marginLeft: 8 }}>
                          {agent.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">{agent.tickets}</TableCell>
                    <TableCell align="center">{agent.responses}</TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        style={{
                          fontWeight: 700,
                          color: agent.avgRating >= 4.5 ? "#22c55e" : agent.avgRating >= 4 ? ACCENT : "#f59e0b",
                        }}
                      >
                        {agent.avgRating}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${agent.satisfaction}%`}
                        size="small"
                        style={{
                          backgroundColor:
                            agent.satisfaction >= 90 ? "#dcfce7" :
                            agent.satisfaction >= 80 ? "#fef3c7" : "#fee2e2",
                          color:
                            agent.satisfaction >= 90 ? "#16a34a" :
                            agent.satisfaction >= 80 ? "#d97706" : "#dc2626",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex">{starIcons(Math.round(agent.avgRating))}</Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Recent feedback */}
      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>Avaliações Recentes</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 700 }}>Ticket</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Contato</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Agente</TableCell>
                <TableCell style={{ fontWeight: 700 }} align="center">Nota</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Comentário</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Data</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {CSAT_DATA.recentFeedback.map((fb) => (
                <TableRow key={fb.id} hover>
                  <TableCell>
                    <Typography variant="body2" style={{ color: ACCENT, fontWeight: 600 }}>
                      {fb.ticket}
                    </Typography>
                  </TableCell>
                  <TableCell>{fb.contact}</TableCell>
                  <TableCell>{fb.agent}</TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center">{starIcons(fb.rating)}</Box>
                  </TableCell>
                  <TableCell style={{ maxWidth: 350 }}>
                    <Typography variant="body2" noWrap>{fb.comment}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="textSecondary">{fb.date}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
};

export default CustomerSatisfaction;
